import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../../config";
import { getMessage } from "../../data/locales";
import { buildActivityLogQueryString } from "./activityLogQueryParams";
import { unwrapAccionesListPayload } from "../../utils/unwrapAccionesListPayload";
import { getAccionSistemaTipoLabel } from "../../utils/accionSistemaLabels";
import { getAccionTipoFilterOptions } from "../../utils/accionesFiltroCatalog";
import { formatActivityLogDateCell } from "../../utils/formatActivityLogDate";
import "./ActivityLogQuery.css";
import ViewportSelect from "../../components/ViewportSelect";

const RECURSO_OPTIONS = [
  "",
  "usuario",
  "curso",
  "test",
  "archivo",
  "imagen",
  "pedido",
  "mensaje",
  "configuracion",
  "anuncio",
  "testimonio",
];

const DEFAULT_LIMIT = 100;
const MIN_LIMIT = 1;
const MAX_LIMIT = 500;

const INITIAL_FILTERS = {
  tipo: "",
  recurso: "",
  ejecutadoPor: "",
  desde: "",
  hasta: "",
  limit: DEFAULT_LIMIT,
  page: 1,
};

/**
 * @param {string | number} raw
 * @returns {number}
 */
function clampActivityLogLimit(raw) {
  const n =
    typeof raw === "number" ? raw : Number.parseInt(String(raw), 10);
  if (!Number.isFinite(n)) return DEFAULT_LIMIT;
  return Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.floor(n)));
}

/**
 * @param {object} filters
 * @returns {object}
 */
function buildQueryPayload(filters) {
  const limit = clampActivityLogLimit(filters.limit);
  const pageNum = Math.max(
    1,
    Number.parseInt(String(filters.page ?? 1), 10) || 1,
  );
  const offset = (pageNum - 1) * limit;
  return {
    tipo: filters.tipo,
    recurso: filters.recurso,
    ejecutadoPor: filters.ejecutadoPor,
    desde: filters.desde,
    hasta: filters.hasta,
    limit: String(limit),
    offset: offset > 0 ? String(offset) : "",
  };
}

/**
 * Date inputs use YYYY-MM-DD; both empty is valid; single date is valid.
 * @param {string} desde
 * @param {string} hasta
 * @returns {boolean}
 */
function isDateRangeFilterValid(desde, hasta) {
  if (desde === "" || hasta === "") {
    return true;
  }
  return desde <= hasta;
}

function ActivityLogQuery() {
  const tipoFilterOptions = useMemo(
    () => getAccionTipoFilterOptions(getMessage("activityLog.any")),
    [],
  );
  const fetchAbortRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tipo, setTipo] = useState(INITIAL_FILTERS.tipo);
  const [recurso, setRecurso] = useState(INITIAL_FILTERS.recurso);
  const [ejecutadoPor, setEjecutadoPor] = useState(INITIAL_FILTERS.ejecutadoPor);
  const [desde, setDesde] = useState(INITIAL_FILTERS.desde);
  const [hasta, setHasta] = useState(INITIAL_FILTERS.hasta);
  const [limit, setLimit] = useState(INITIAL_FILTERS.limit);
  const [page, setPage] = useState(INITIAL_FILTERS.page);

  const fetchLogs = useCallback(async (filters) => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    setError("");
    try {
      const payload = buildQueryPayload(filters);
      const qs = buildActivityLogQueryString(payload);
      const url = qs ? `${API_URL}/api/admin/activity-logs?${qs}` : `${API_URL}/api/admin/activity-logs`;
      const r = await fetch(url, {
        credentials: "include",
        signal: controller.signal,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg =
          typeof data.error === "string"
            ? data.error
            : getMessage("activityLog.loadError");
        setError(msg);
        setRows([]);
        return;
      }
      setRows(unwrapAccionesListPayload(data));
    } catch (err) {
      if (err && typeof err === "object" && "name" in err && err.name === "AbortError") {
        return;
      }
      setError(getMessage("activityLog.networkError"));
      setRows([]);
    } finally {
      if (fetchAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchLogs({ ...INITIAL_FILTERS }).catch(() => {});
    return () => {
      fetchAbortRef.current?.abort();
    };
  }, [fetchLogs]);

  const sanitizedLimit = clampActivityLogLimit(limit);
  const canGoPrev = page > 1;
  const canGoNext = rows.length >= sanitizedLimit;

  const clearFilters = () => {
    fetchAbortRef.current?.abort();
    setRows([]);
    setTipo(INITIAL_FILTERS.tipo);
    setRecurso(INITIAL_FILTERS.recurso);
    setEjecutadoPor(INITIAL_FILTERS.ejecutadoPor);
    setDesde(INITIAL_FILTERS.desde);
    setHasta(INITIAL_FILTERS.hasta);
    setLimit(INITIAL_FILTERS.limit);
    setPage(INITIAL_FILTERS.page);
    setError("");
  };

  const loadLogsForPage = useCallback(
    (targetPage) => {
      if (!isDateRangeFilterValid(desde, hasta)) {
        setError(getMessage("activityLog.dateRangeError"));
        return;
      }
      setPage(targetPage);
      fetchLogs({
        tipo,
        recurso,
        ejecutadoPor,
        desde,
        hasta,
        limit,
        page: targetPage,
      }).catch(() => {});
    },
    [desde, hasta, tipo, recurso, ejecutadoPor, limit, fetchLogs],
  );

  const runApply = () => {
    loadLogsForPage(1);
  };

  const goPrevPage = () => {
    loadLogsForPage(Math.max(1, page - 1));
  };

  const goNextPage = () => {
    loadLogsForPage(page + 1);
  };

  return (
    <div className="activity-log-page">
      <h1>{getMessage("activityLog.title")}</h1>
      <p className="muted">{getMessage("activityLog.subtitle")}</p>

      <div className="activity-log-filters">
        <label>
          {getMessage("activityLog.filterTipo")}
          <ViewportSelect value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {tipoFilterOptions.map(({ value, label }) => (
              <option key={value || "any-tipo"} value={value}>
                {label}
              </option>
            ))}
          </ViewportSelect>
        </label>
        <label>
          {getMessage("activityLog.filterRecurso")}
          <ViewportSelect value={recurso} onChange={(e) => setRecurso(e.target.value)}>
            {RECURSO_OPTIONS.map((v) => (
              <option key={v || "any-res"} value={v}>
                {v
                  ? getMessage(`activityLog.recursoLabels.${v}`)
                  : getMessage("activityLog.any")}
              </option>
            ))}
          </ViewportSelect>
        </label>
        <label>
          {getMessage("activityLog.filterEjecutadoPor")}
          <input
            type="text"
            value={ejecutadoPor}
            onChange={(e) => setEjecutadoPor(e.target.value)}
            placeholder={getMessage("activityLog.placeholderUserId")}
            autoComplete="off"
          />
        </label>
        <label>
          {getMessage("activityLog.filterDesde")}
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label>
          {getMessage("activityLog.filterHasta")}
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <label>
          {getMessage("activityLog.filterLimit")}
          <input
            type="number"
            min={MIN_LIMIT}
            max={MAX_LIMIT}
            value={limit}
            onChange={(e) => setLimit(clampActivityLogLimit(e.target.value))}
          />
        </label>
      </div>

      <div className="activity-log-actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={runApply}
          disabled={loading}
        >
          {loading ? getMessage("activityLog.loading") : getMessage("activityLog.apply")}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={clearFilters}
          disabled={loading}
        >
          {getMessage("activityLog.clear")}
        </button>
        <Link className="btn btn-link btn-sm" to="/gestion-director">
          {getMessage("activityLog.back")}
        </Link>
      </div>

      <div className="activity-log-pagination">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={goPrevPage}
          disabled={loading || !canGoPrev}
        >
          {getMessage("activityLog.pagePrev")}
        </button>
        <span className="activity-log-page-label">
          {getMessage("activityLog.pageLabel", { page })}
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={goNextPage}
          disabled={loading || !canGoNext}
        >
          {getMessage("activityLog.pageNext")}
        </button>
      </div>

      {error ? (
        <div className="activity-log-msg error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="activity-log-table-wrap">
        <table className="activity-log-table">
          <thead>
            <tr>
              <th>{getMessage("activityLog.colFecha")}</th>
              <th>{getMessage("activityLog.colTipo")}</th>
              <th>{getMessage("activityLog.colRecurso")}</th>
              <th>{getMessage("activityLog.colEjecutadoPor")}</th>
              <th>{getMessage("activityLog.colObservacion")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={5}>{getMessage("activityLog.empty")}</td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={row._id}>
                <td>{formatActivityLogDateCell(row.fecha)}</td>
                <td>{getAccionSistemaTipoLabel(row.tipo)}</td>
                <td>{row.recurso}</td>
                <td>
                  {row.ejecutadoPor?.nombre ? `${row.ejecutadoPor.nombre} (${row.ejecutadoPor.rol || "—"})` : "—"}
                </td>
                <td>{row.observacion || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ActivityLogQuery;
